// Layer 3 (Transformation): control-plan band resolution for rule-set sections.
// Pure derivation — no React, no engine mutation.
// See docs/schema/control-plan.md, docs/engines/rule-set.md § Band lifecycle.

import type { MetDiagnosticsSection } from '@/versions/v1/schema/met-diagnostics-cause';
import type { Band } from '@/versions/v1/schema/reading';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { ControlPlanRow } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
import { CONTROL_PLAN_ZN_ROWS } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
import { ORE_ZONES, ORE_ZONE_SCHEDULE, oreZoneAt } from '@/versions/v1/shared/bunker-hill-ore-zones';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { windowMean } from '@/versions/v1/shared/series.derive';
import { INITIAL_SESSION_STATE } from '@/versions/v1/schema/scenario';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';

export type ControlPlanSectionId = MetDiagnosticsSection | 'equipment';

export type BandResolution = {
  band: Band;
  unit: string;
  zoneLabel: string | null;
  zoneId: string | null;
  fixed: boolean;
  row: ControlPlanRow;
  sourceId: string;
  tooltip: string;
};

const READING_BY_ID = new Map(READINGS.map((row) => [row.id, row]));

function unitSuffix(unit: string): string {
  if (unit === '%') return ' %';
  if (unit === '% Zn' || unit === '% Pb') return ` ${unit}`;
  return ` ${unit}`;
}

export function formatBand(band: Band, unit: string): string {
  if (band.lo !== undefined && band.hi !== undefined) {
    return `${band.lo}–${band.hi}${unitSuffix(unit)}`;
  }
  if (band.lo !== undefined) {
    return `≥ ${band.lo}${unitSuffix(unit)}`;
  }
  if (band.hi !== undefined) {
    return `≤ ${band.hi}${unitSuffix(unit)}`;
  }
  return '—';
}

/** Derive the proposed Zn-recovery band for the high-silver zone — mean of
 * `rs-zn-rec` from the zone change (05 Jun 18:00) through the cursor, rounded
 * to one decimal [syn]. Only oz-ag feed days contribute — not Main stope days
 * in a plant-wide seven-day window. */
export function proposedZnRecBandForAgZone(
  cursor: SeriesTime = DEFAULT_CURSOR,
): { band: Band; basis: string } {
  const agStart = ORE_ZONE_SCHEDULE.find((entry) => entry.zoneId === 'oz-ag')?.from ?? cursor;
  const state = { ...INITIAL_SESSION_STATE, cursor };
  const mean = windowMean('rs-zn-rec', state, agStart);
  const rounded = Math.round(mean * 10) / 10;
  return {
    band: { lo: rounded },
    basis: `High-silver zone 7 d mean Zn recovery ${rounded.toFixed(1)} % [syn]`,
  };
}

export function liveControlPlanRows(
  sessionRows: readonly ControlPlanRow[] = [],
): readonly ControlPlanRow[] {
  if (sessionRows.length === 0) return CONTROL_PLAN_ZN_ROWS;
  const byId = new Map(CONTROL_PLAN_ZN_ROWS.map((row) => [row.id, row]));
  for (const row of sessionRows) {
    byId.set(row.id, row);
  }
  return [...byId.values()];
}

export function bandForSection(
  sectionId: ControlPlanSectionId,
  cursor: SeriesTime,
  rows: readonly ControlPlanRow[] = CONTROL_PLAN_ZN_ROWS,
): BandResolution | undefined {
  const zone = oreZoneAt(cursor);
  const sectionRows = rows.filter((row) => row.sectionId === sectionId);
  if (sectionRows.length === 0) return undefined;

  const fixedRow = sectionRows.find((row) => row.fixed);
  if (fixedRow) {
    const reading = fixedRow.readingId ? READING_BY_ID.get(fixedRow.readingId) : undefined;
    const unit = reading?.unit ?? '°C';
    return {
      band: fixedRow.specOverlay,
      unit,
      zoneLabel: null,
      zoneId: null,
      fixed: true,
      row: fixedRow,
      sourceId: fixedRow.sourceId,
      tooltip: `${fixedRow.id} · ${fixedRow.instrumentTag} · trip limit · source: control plan`,
    };
  }

  const zoned =
    sectionRows.find((row) => row.zoneId && zone && row.zoneId === zone.id) ??
    sectionRows.find((row) => !row.zoneId) ??
    sectionRows[0];

  const reading = zoned.readingId ? READING_BY_ID.get(zoned.readingId) : undefined;
  const unit = reading?.unit ?? '%';
  const zoneLabel =
    zoned.zoneId && oreZoneAt(cursor)?.id === zoned.zoneId
      ? oreZoneAt(cursor)?.label.split(' · ')[0] ?? null
      : zoned.zoneId
        ? ORE_ZONES.find((row) => row.id === zoned.zoneId)?.label.split(' · ')[0] ?? zoned.zoneId
        : null;

  const zoneAtCursor =
    zone && zoned.zoneId && zone.id === zoned.zoneId
      ? zone.label.split(' · ')[0] ?? zone.label
      : zoneLabel;

  return {
    band: zoned.specOverlay,
    unit,
    zoneLabel: zoned.zoneId ? zoneAtCursor : null,
    zoneId: zoned.zoneId ?? null,
    fixed: false,
    row: zoned,
    sourceId: zoned.sourceId,
    tooltip: `${zoned.id} · ${zoned.instrumentTag} · ${formatBand(zoned.specOverlay, unit)} · source: control plan`,
  };
}

export function bandLineForSection(
  sectionId: ControlPlanSectionId,
  cursor: SeriesTime,
  rows?: readonly ControlPlanRow[],
): string | null {
  const resolved = bandForSection(sectionId, cursor, rows);
  if (!resolved) return null;
  return formatBand(resolved.band, resolved.unit);
}
